import { Link } from "react-router-dom";
import { ArrowRight, Compass, Eye, Globe2, Award, Zap, ShieldCheck } from "lucide-react";
import Reveal, { SectionHeading } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { useSEO } from "@/hooks/useSEO";

const values = [
  { icon: Award, title: "Global Brand Catalog", body: "Hundreds of trusted international digital brands." },
  { icon: Zap, title: "Instant Fulfillment", body: "Fast and seamless digital gift delivery worldwide." },
  { icon: ShieldCheck, title: "Reliable Distribution", body: "Consistent fulfillment for enterprise gifting programs." },
  { icon: Globe2, title: "Worldwide Reach", body: "Serving businesses across Europe, Asia, Middle East & Africa." },
];

export default function About() {
  useSEO(
    "About Vowpin Global — Connecting Businesses with Leading Digital Brands",
    "Vowpin Global is a worldwide digital gifting company distributing gift cards, gaming vouchers, prepaid rewards, and branded digital experiences across 120+ countries."
  );
  return (
    <main className="pt-24">
      {/* Hero */}
      <section className="relative overflow-hidden bg-white py-20 lg:py-28">
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#c98a00]">About Vowpin Global</p>
            <h1 className="mt-5 max-w-4xl font-display text-4xl font-semibold leading-[1.08] tracking-tight text-[#111827] sm:text-5xl lg:text-6xl">
              Connecting Businesses with the World's <span className="text-gradient-blue">Leading Digital Brands</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#64748b] md:text-lg">
              Vowpin Global is a worldwide digital gifting company specializing in the distribution
              of gift cards, gaming vouchers, prepaid rewards, and branded digital experiences.
            </p>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#64748b] md:text-lg">
              We partner with businesses to deliver premium gifting solutions that enhance customer
              engagement, employee recognition, and global reward programs through a trusted
              international catalog.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="border-y border-[#e8ecf4] bg-[#f7f9fc] py-24">
        <div className="mx-auto grid max-w-7xl gap-8 px-6 md:grid-cols-2 lg:px-10">
          <Reveal>
            <div className="glow-card h-full rounded-[20px] p-10" data-testid="mission-card">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#f4a900]/[0.12] ring-1 ring-[#f4a900]/30">
                <Compass className="h-6 w-6 text-[#c98a00]" />
              </div>
              <h2 className="mt-6 font-display text-2xl font-semibold text-[#111827]">Our Mission</h2>
              <p className="mt-4 text-base leading-relaxed text-[#64748b]">
                To make digital gifting simple, global, and accessible for every business.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="glow-card h-full rounded-[20px] p-10" data-testid="vision-card">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0816a8]/[0.06] ring-1 ring-[#0816a8]/15">
                <Eye className="h-6 w-6 text-[#0816a8]" />
              </div>
              <h2 className="mt-6 font-display text-2xl font-semibold text-[#111827]">Our Vision</h2>
              <p className="mt-4 text-base leading-relaxed text-[#64748b]">
                To become the world's most trusted partner for digital gift card distribution.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* What sets us apart */}
      <section className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeading
            eyebrow="What sets us apart"
            title="Premium service, global scale"
            sub="Every program we run is backed by a catalog businesses trust and fulfillment teams can rely on."
          />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.07}>
                <div className="glow-card h-full rounded-[20px] p-8" data-testid={`about-value-${i}`}>
                  <v.icon className="h-6 w-6 text-[#0816a8]" />
                  <h3 className="mt-5 font-display text-lg font-semibold text-[#111827]">{v.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-[#64748b]">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/contact"
              data-testid="about-talk-to-sales-btn"
              className="group mt-12 inline-flex items-center gap-2 rounded-full bg-[#0816a8] px-8 py-4 text-sm font-semibold text-white shadow-[0_10px_28px_-8px_rgba(8,22,168,0.5)] transition-all duration-300 hover:bg-[#0a1bc4] hover:shadow-[0_14px_36px_-8px_rgba(8,22,168,0.6)]"
            >
              Talk to Sales
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </main>
  );
}
